<script setup lang="ts" generic="T">
import Multiselect from "vue-multiselect";
import type { PropType } from "vue";
import SvgIcon from "~/components/Common/SvgIcon.vue";
import type { LocationModel } from "@simbelapp/order-sdk";
import Tooltip from "~/components/Common/Tooltip.vue";
import { safeExternalUrl } from "~/utils/helpers/sanitize";

const isLoading = ref(false);
const searchQuery = ref("");
const multiselectRef = ref<HTMLElement | null>(null);
const scrollContainer = ref<HTMLElement | null>(null);
const offset = ref(0);
const emit = defineEmits(["update:modelValue", "clickAdd", "searchValue", "open"]);

const props = defineProps({
  options: {
    type: [Array<T>],
    default() {
      return [];
    },
  },
  location: {
    type: Object as PropType<LocationModel>,
    default: null,
    require: false,
  },
  servicesAPI: {
    type: Function,
    default: null,
  },
  store: {
    type: Object,
    default: null,
  },
  storeObject: {
    type: String,
    default: null,
  },
  modelValue: {
    type: null,
  },
  lazyLoad: {
    type: Boolean,
    default: false,
  },
  add: {
    type: Boolean,
    default: false,
  },
  icon: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
    default: "name",
  },
  showNoOptions: {
    type: Boolean,
    default: false,
  },
  showNoResults: {
    type: Boolean,
    default: true,
  },
  placeholder: {
    type: String,
    default: "Choisir une option",
  },
  error: {
    type: Boolean,
    default: false,
  },
  openFetch: {
    type: Boolean,
    default: false,
  },
  delete: {
    type: Boolean,
    default: true,
  },
});

/** Whether this dropdown fetches its own options, or just renders the static `options` it was given. */
const hasAsyncFetch = computed(() => !!props.servicesAPI);

const currentLocation = ref<any>({
  locationId: "",
  locationName: "",
  locationStreet: "",
  locationCity: "",
  locationState: "",
  locationPostalCode: "",
  locationCountry: "",
  locationAccess: "",
});
onMounted(() => {
  if (hasAsyncFetch.value) {
    // Initial page only. The scroll listener is attached in open() — the multiselect content
    // wrapper doesn't exist in the DOM until the dropdown opens (so it's null here).
    asyncFind("", true);
  }
  if (props.modelValue && props.modelValue.hasOwnProperty("roomName")) {
    currentLocation.value = props.location;
  } else {
    currentLocation.value = null;
  }

  if (props.modelValue && Object.keys(props.modelValue).length === 1) {
    (selectRef.value as any).search = props.modelValue.locationName;
  }
});

interface QueryParams {
  [key: string]: any;
}

function updateValue(newValue: any) {
  if (newValue && newValue.hasOwnProperty("roomName")) {
    currentLocation.value = props.options.find((option: any) => option.locationId === newValue.roomLocationId);
  } else if (newValue && newValue.hasOwnProperty("locationId")) {
    currentLocation.value = props.options.find((option: any) => option.locationId === newValue.locationId);
  } else {
    currentLocation.value = null;
  }
  emit("update:modelValue", newValue);
}

async function asyncFind(query: string, init?: boolean) {
  getSearchQuery(query);
  // Imperative fetch — do NOT wrap in useAsyncData (since Nuxt 4 it caches by key and replays
  // the cached result without re-running the handler, and the static "list" key was shared
  // across every instance). servicesAPI (SDK) is fresh on every call.
  if (!init) isLoading.value = true;
  const queryParams: QueryParams = {
    limit: 10,
    offset: offset.value,
    ...{ search: query },
  };

  // servicesAPI throws on error (unlike the old useAsyncData, which captured errors in a ref).
  // Guard the call so a failed fetch leaves the list untouched and still resets isLoading below —
  // otherwise handleScroll's `isLoading === false` gate stays closed and infinite-scroll silently
  // dies for the rest of this dropdown's lifetime.
  let items: any[] = [];
  try {
    const api: any = await props.servicesAPI?.(queryParams);
    items = api?.data?.value?.data ?? api?.data ?? [];
  } catch {
    console.error("LocationDropdown asyncFind failed");
  }
  if (init) {
    props.store[props.storeObject] = [];
    props.store[props.storeObject]?.push(...items);
  } else {
    for (const item of items) {
      if (props.store[props.storeObject]?.some((existingItem: any) => existingItem.locationId === item.locationId)) {
        const existingItem = props.store[props.storeObject]?.find(
          (existingItem: any) => existingItem.locationId === item.locationId,
        );
        if (existingItem) {
          existingItem.rooms.push(
            ...item.rooms.filter(
              (room: { roomId: string }) =>
                !existingItem.rooms.some((r: { roomId: string }) => r.roomId === room.roomId),
            ),
          );
        }
      } else {
        props.store[props.storeObject]?.push(item);
      }
    }
  }

  offset.value += 10;
  if (!init) isLoading.value = false;
}

function getSearchQuery(query: string) {
  searchQuery.value = query;
}
const search = ref<string>("");
const lastSearch = ref<string>("");

async function searchChange(q: string) {
  search.value = q;
  offset.value = 0;
  lastSearch.value = q;

  if (hasAsyncFetch.value) {
    await asyncFind(q, true).then(async () => {
      await nextTick(() => {
        if (scrollContainer.value) {
          scrollContainer.value.scrollTop = 0;
        }
      });
    });
    getSearchQuery(q);
  } else if (props.add) {
    getSearchQuery(q);
  } else {
    return null;
  }
}

async function handleScroll() {
  if (
    scrollContainer.value &&
    scrollContainer.value.scrollTop + scrollContainer.value.clientHeight >= scrollContainer.value.scrollHeight - 10 &&
    isLoading.value === false
  ) {
    await asyncFind(searchQuery.value);
  }
}

function open() {
  nextTick(() => {
    // Only wire lazy-loading when this dropdown actually fetches. Static-options usages have
    // nothing to page.
    if (!hasAsyncFetch.value) return;
    // vue-multiselect only mounts .multiselect__content-wrapper while the dropdown is open, so it
    // isn't in the DOM at onMounted. (Re)acquire it and attach the infinite-scroll listener on each
    // open — otherwise handleScroll never fires and lazy-loading past the first page never happens.
    const wrapper = multiselectRef.value?.querySelector(".multiselect__content-wrapper") as HTMLElement | null;
    if (wrapper && wrapper !== scrollContainer.value) {
      scrollContainer.value?.removeEventListener("scroll", handleScroll);
      scrollContainer.value = wrapper;
      wrapper.addEventListener("scroll", handleScroll);
    }
  });
  if (props.openFetch) {
    emit("open");
  }
}
function clearAll() {
  props.modelValue.value = null;
}

const flattenedOptions = computed(() => {
  return props.options.reduce((acc: any, group: any) => {
    acc.push({
      locationId: group.locationId,
      locationName: group.locationName,
      isGroup: true,
      locationStreet: group.locationStreet,
      locationCity: group.locationCity,
      locationState: group.locationState,
      locationPostalCode: group.locationPostalCode,
      locationCountry: group.locationCountry,
      locationAccess: group.locationAccess,
      locationManualAddress: group.locationManualAddress,
    });
    if (group.rooms && Array.isArray(group.rooms)) {
      group.rooms.forEach((child: any) => {
        acc.push({
          roomId: child.roomId,
          roomName: child.roomName,
          isGroup: false,
          roomCapacity: child.roomCapacity,
          roomAccess: child.roomAccess,
          roomEquipment: child.roomEquipment,
          roomLocationId: child.roomLocationId,
          locationName: child.roomName,
        });
      });
    }
    return acc;
  }, []);
});

function addTag(newTag: string) {
  emit("update:modelValue", newTag);
  (selectRef.value as any).search = newTag;
}

const selectRef = ref<InstanceType<typeof Multiselect> | null>(null);

function onClose() {
  if (search.value && !currentLocation.value) {
    addTag(search.value);
  }
}

function handleKeyDown(event: KeyboardEvent) {
  if (event.key === "Enter" && search.value) {
    event.preventDefault();
    addTag(search.value);
    (selectRef.value as any).$el.querySelector("input").blur();
  }
}

watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue === null) {
      (selectRef.value as any).search = "";
    }
  },
);
</script>
<template>
  <div ref="multiselectRef">
    <multiselect
      ref="selectRef"
      :model-value="props.modelValue"
      :options="flattenedOptions"
      :loading="isLoading"
      track-by="locationName"
      label="locationName"
      :placeholder="placeholder"
      :max-height="200"
      :show-labels="false"
      :show-pointer="false"
      open-direction="bottom"
      :show-no-results="showNoResults"
      :disabled="disabled"
      :show-no-options="showNoOptions"
      class="singlegrouped"
      :internal-search="false"
      :preserve-search="true"
      :class="{
        error: props.error,
        hiddenSearchOption: search.length,
        'hiddenmultiselect__content-wrapper': search.length && flattenedOptions.length === 0,
      }"
      :taggable="false"
      @search-change="searchChange"
      @update:modelValue="updateValue"
      @open="open"
      @tag="addTag"
      @close="onClose"
      @keydown="handleKeyDown"
    >
      <template #singleLabel="{ option }">
        <div v-if="option.hasOwnProperty('locationId')" class="flex flex-row gap-2 items-center truncate">
          <Tooltip v-if="option.locationName" :position="'top-right'" append-to-body minified class="truncate">
            <template #img>
              <span class="body_regular cursor-pointer truncate">{{ option.locationName }}</span>
            </template>
            <template #content>
              <div class="max-w-[420px] break-words">
                <span class="body_regular">{{ option.locationName }}</span>
              </div>
            </template>
          </Tooltip>

          <Tooltip v-if="option.locationManualAddress" :position="'top-right'" append-to-body minified class="truncate">
            <template #img>
              <span v-if="option.locationManualAddress" class="body_regular cursor-pointer truncate">
                - {{ option.locationManualAddress }}</span
              >
            </template>
            <template #content>
              <div class="max-w-[420px] break-words">
                <span class="body_regular">{{ option.locationManualAddress }}</span>
              </div>
            </template>
          </Tooltip>

          <a
            v-if="option.locationAccess"
            :href="safeExternalUrl(option.locationAccess)"
            target="_blank"
            class="text-secondary-color flex gap-1 items-center"
            @mousedown.prevent.stop
          >
            <SvgIcon name="link" class="w-4 h-4" />
            <span class="body_regular underline">Plan d'accès</span>
          </a>
        </div>
        <div v-if="option.hasOwnProperty('roomName')" class="flex flex-col gap-2 truncate">
          <div class="flex flex-row gap-2 items-center">
            <Tooltip
              v-if="currentLocation.locationName"
              :position="'top-right'"
              append-to-body
              minified
              class="truncate"
            >
              <template #img>
                <span class="body_regular cursor-pointer truncate">{{ currentLocation.locationName }}</span>
              </template>
              <template #content>
                <div class="max-w-[420px] break-words">
                  <span class="body_regular">{{ currentLocation.locationName }}</span>
                </div>
              </template>
            </Tooltip>
            <Tooltip
              v-if="currentLocation.locationManualAddress"
              :position="'top-right'"
              append-to-body
              minified
              class="truncate"
            >
              <template #img>
                <span class="body_regular cursor-pointer truncate">- {{ currentLocation.locationManualAddress }}</span>
              </template>
              <template #content>
                <div class="max-w-[420px] break-words">
                  <span class="body_regular">{{ currentLocation.locationManualAddress }}</span>
                </div>
              </template>
            </Tooltip>

            <a
              v-if="currentLocation.locationAccess"
              :href="safeExternalUrl(currentLocation.locationAccess)"
              target="_blank"
              class="text-secondary-color flex gap-1 items-center"
              @mousedown.prevent.stop
            >
              <SvgIcon name="link" class="w-4 h-4" />
              <span class="body_regular underline">Plan d'accès</span>
            </a>
          </div>
          <div class="flex flex-row gap-2 items-center">
            <Tooltip :position="'top-right'" append-to-body minified class="truncate">
              <template #img>
                <span class="body_regular cursor-pointer truncate">{{ option.roomName }}</span>
              </template>
              <template #content>
                <div class="max-w-[420px] break-words">
                  <span class="body_regular">{{ option.roomName }}</span>
                </div>
              </template>
            </Tooltip>

            <Tooltip :position="'top-right'" append-to-body minified>
              <template #img>
                <div class="flex flex-row gap-1 text-secondary-color items-center">
                  <SvgIcon name="group" class="w-4 h-4"></SvgIcon>
                  <span class="body_regular cursor-pointer">{{ option.roomCapacity ?? "-" }}</span>
                </div>
              </template>
              <template #content>
                <div class="max-w-[420px] break-words">
                  <span class="body_regular">{{ option.roomCapacity ?? "-" }}</span>
                </div>
              </template>
            </Tooltip>

            <Tooltip v-if="option.roomAccess" :position="'top-right'" append-to-body minified class="truncate">
              <template #img>
                <div class="flex flex-row gap-1 text-secondary-color items-center truncate">
                  <SvgIcon name="target-menu" class="min-w-4 min-h-4 w-4 h-4"></SvgIcon>
                  <span class="body_regular cursor-pointer truncate">{{ option.roomAccess }}</span>
                </div>
              </template>
              <template #content>
                <div class="max-w-[420px] break-words">
                  <span class="body_regular">{{ option.roomAccess }}</span>
                </div>
              </template>
            </Tooltip>

            <Tooltip v-if="option.roomEquipment" :position="'top-right'" append-to-body minified class="truncate">
              <template #img>
                <div class="flex flex-row gap-1 text-secondary-color items-center truncate">
                  <SvgIcon name="equipment" class="min-w-4 min-h-4 w-4 h-4"></SvgIcon>
                  <span class="body_regular cursor-pointer truncate">{{ option.roomEquipment }}</span>
                </div>
              </template>
              <template #content>
                <div class="max-w-[420px] break-words">
                  <span class="body_regular">{{ option.roomEquipment }}</span>
                </div>
              </template>
            </Tooltip>
          </div>
        </div>
      </template>
      <template #option="{ option }">
        <div v-if="option.isGroup" class="flex flex-row items-center gap-2 option">
          <Tooltip :position="'top-right'" append-to-body minified class="truncate">
            <template #img>
              <span class="body_med text-secondary-color truncate">{{ option.locationName }}</span>
            </template>
            <template #content>
              <div class="max-w-[420px] break-words">
                <span class="body_regular">{{ option.locationName }}</span>
              </div>
            </template>
          </Tooltip>

          <Tooltip v-if="option.locationManualAddress" :position="'top-right'" append-to-body minified class="truncate">
            <template #img>
              <span class="text-secondary-color truncate">- {{ option.locationManualAddress }}</span>
            </template>
            <template #content>
              <div class="max-w-[420px] break-words">
                <span class="body_regular">{{ option.locationManualAddress }}</span>
              </div>
            </template>
          </Tooltip>

          <a
            v-if="option.locationAccess"
            :href="safeExternalUrl(option.locationAccess)"
            target="_blank"
            class="text-secondary-color flex gap-1 items-center"
          >
            <SvgIcon name="link" class="w-4 h-4" />
            <span class="body_regular underline">Plan d'accès</span>
          </a>
        </div>

        <div v-else-if="option.roomName" class="ml-6 flex flex-row gap-2 items-center child-option">
          <Tooltip :position="'top-right'" append-to-body minified class="truncate">
            <template #img>
              <span class="text-secondary-color truncate">{{ option.roomName }}</span>
            </template>
            <template #content>
              <div class="max-w-[420px] break-words">
                <span class="body_regular">{{ option.roomName }}</span>
              </div>
            </template>
          </Tooltip>
          <div class="flex flex-row gap-1 text-grey-medium-color items-center">
            <SvgIcon name="group" class="w-4 h-4"></SvgIcon>
            <span class="body_regular cursor-pointer">{{ option.roomCapacity ?? "-" }}</span>
          </div>

          <Tooltip v-if="option.roomAccess" :position="'top-right'" append-to-body minified class="truncate">
            <template #img>
              <div class="flex flex-row gap-1 text-grey-medium-color items-center truncate">
                <div class="w-4 h-4">
                  <SvgIcon name="target-menu" class="w-4 h-4"></SvgIcon>
                </div>
                <span class="body_regular cursor-pointer truncate">{{ option.roomAccess }}</span>
              </div>
            </template>
            <template #content>
              <div class="max-w-[420px] break-words">
                <span class="body_regular">{{ option.roomAccess }}</span>
              </div>
            </template>
          </Tooltip>
          <Tooltip v-if="option.roomEquipment" :position="'top-right'" append-to-body minified class="truncate">
            <template #img>
              <div class="flex flex-row gap-1 text-grey-medium-color items-center truncate">
                <div class="w-4 h-4">
                  <SvgIcon name="equipment" class="w-4 h-4"></SvgIcon>
                </div>
                <span class="body_regular cursor-pointer truncate">{{ option.roomEquipment }}</span>
              </div>
            </template>
            <template #content>
              <div class="max-w-[420px] break-words">
                <span class="body_regular">{{ option.roomEquipment }}</span>
              </div>
            </template>
          </Tooltip>
        </div>
      </template>
      <template v-if="props.modelValue" #clear="props">
        <div class="multiselect__clear" @mousedown.prevent.stop="clearAll()">
          <SvgIcon
            class="text-secondary-color hover:text-primary-blue-color"
            name="close"
            @mousedown.prevent.stop="updateValue(null)"
          ></SvgIcon>
        </div>
      </template>
      <template v-if="props.options.length" #caret="{ toggle, isOpen }">
        <span class="multiselect__select">
          <SvgIcon class="w-full h-full" name="arrow_down" @mousedown.prevent.stop="toggle"></SvgIcon>
        </span>
      </template>
    </multiselect>
  </div>
</template>

<style lang="scss" scoped>
@use "assets/scss/dropdowns/multiselect";
</style>
